// Reckless forces a creature into the attacker list every combat. The
// SPA mirrors that by pre-seeding the player's attacker selection so the
// creature shows up already-selected during Declare Attackers. These
// helpers compute that forced set; they live here (rather than inline in
// BattlePage) so the turn-gating logic is unit-testable.

import type { GameView } from "./cardView";
import type { CardView } from "./cardView";
import type { PlayerId } from "../api/types";

export function hasReckless(card: CardView): boolean {
  return (card.keywords ?? []).some((k) => k.name === "reckless");
}

// Set of the viewer's own creatures the engine will force into the
// attacker list this combat — currently just Reckless creatures that are
// otherwise eligible to swing (`can_attack` already factors in summoning
// sickness and Seal). Returned as an entity-id set so callers can
// union/diff it against the player's freely-chosen selection.
//
// Only ever non-empty during the viewer's OWN Declare Attackers step.
// The wire's `can_attack` is turn-agnostic (the engine only ever queries
// it for the active seat), so a Reckless creature still reports
// `can_attack = true` on the opponent's turn. Forcing it into the
// selection then would paint it red and lunge it at the opponent's face
// during a turn it can't attack on. Gate on the pending so that never
// happens.
export function forcedAttackerIds(snap: GameView, viewer: PlayerId): Set<number> {
  const out = new Set<number>();
  if (snap.pending?.kind !== "attackers" || snap.pending.player !== viewer) {
    return out;
  }
  const seat = snap.players[viewer];
  if (!seat) return out;
  for (const card of seat.battle) {
    if (card.can_attack && hasReckless(card)) {
      out.add(card.entity_id);
    }
  }
  return out;
}
